import { v4 as uuidv4 } from "uuid";
import i18n from "i18n";
import {
  CurrentJobModel,
  DetailJobMemberModel,
  FollowingQuestionsModel,
  HealthCoverageModel,
  HouseholdMemberModel,
  ImmigrationModel,
  IncomeQuestionModel,
  IncomeTypeModel,
  OtherIncomeSourceModel,
  PregnancyModel,
  RenewingCoverageModel,
  SeasonalJobModel,
  SelfEmploymentModel,
  TaxFillingModel,
} from "models";
import { CurrentJobEnum, RelationshipEnum } from "enums";
import { GeneralForm } from "./types";
import { HouseholdMembersForm } from "components/pages/DhsPage/Forms/HouseholdMemberListForm/formUtils";
import { calculatePaiMemberAnnualIncome } from "./utils";
import { PAIFormFormType } from "components/pages/DhsPage/Forms/PAIForm/PAIForm";

export const useGetDefaultFormValues = (data: GeneralForm | null) => {
  /** 1.APPLICANT  FORM */
  const getDefaultApplicantFormValues = () => {
    const applicantValues = data?.[1];

    const applicantInitialValues = {
      partnerName: "",
      firstName: "",
      middleName: "",
      lastName: "",
      birthday: "",
      gender: "",
      maritalStatus: "",
      ssn: null,
      phoneNumber: "",
      streetAddress: "",
      city: "",
      state: "",
      zipCode: "",
      county: "",
      preferredLanguage: "",
    };
    return applicantValues ?? applicantInitialValues;
  };

  const defaultApplicantMember = getDefaultApplicantFormValues();

  /** APPLICANT FORM ENDS */

  /** 2.DEFAULT HOUSEHOLD FORM */
  const getDefaultHouseholdMembersValues = () => {
    const applicantValues = data?.[1];
    const householdFormValues = data?.[2];

    const memberListValues = householdFormValues?.members;
    const hasMembers = !!memberListValues?.length;
    const applicantValue = {
      id: uuidv4(),
      firstName: applicantValues?.firstName,
      middleName: applicantValues?.middleName,
      lastName: applicantValues?.lastName,
      birthday: applicantValues?.birthday,
      maritalStatus: i18n.t(applicantValues?.maritalStatus),
      ssn: applicantValues?.ssn,
      isRequestCoverage: false,
      relationship: i18n.t(RelationshipEnum.SELF),
    } as HouseholdMemberModel;

    return {
      members: hasMembers ? memberListValues : [applicantValue],
      householdRelationships: householdFormValues?.householdRelationships || "",
    } as HouseholdMembersForm;
  };
  const defaultHouseholdMembers = getDefaultHouseholdMembersValues();

  /** 2.DEFAULT HOUSEHOLD FORM ENDS*/

  /** 3.DEFAULT PREGNANCY FORM */
  const getDefaultPregnancyValues = () => {
    const householdMembers = data?.[2]?.members;
    const pregnancyMembers = data?.[3]?.pregnancy;

    const pregnancyListFromHouseholdMembers = householdMembers?.map((m) => {
      return {
        id: m.id,
        name: `${m.firstName} ${m.lastName}`,
        expectedBabiesCount: null,
        isPregnant: false,
        birthday: null,
      };
    });
    return (
      pregnancyMembers ??
      (pregnancyListFromHouseholdMembers as PregnancyModel[])
    );
  };

  const defaultPregnancyMembers = getDefaultPregnancyValues();

  /** 3. DEFAULT PREGNANCY FORM ENDS */

  /** 4 .DEFAULT TAX FILLING FORM */
  const getDefaultTaxFillingValues = () => {
    const householdMembers = data?.[2]?.members;
    const taxFillingMembers = data?.[4]?.taxFillingMembers;

    const taxFillingFromHouseholdMembers = householdMembers?.map((tm) => {
      return {
        id: tm.id,
        name: `${tm.firstName} ${tm.lastName}`,
        isMarriedFilingJointly: null,
        claimantOfDependent: "",
        outsideHouseholdTaxDependent: false,
        isClaimedByNoncustodialParent: false,
      };
    });
    return (
      taxFillingMembers ?? (taxFillingFromHouseholdMembers as TaxFillingModel[])
    );
  };

  const defaultTaxFillingMembers = getDefaultTaxFillingValues();

  /** 4. DEFAULT TAX FILLING FORM ENDS */

  /** 5. DEFAULT INCOME QUESTION FORM */
  const getDefaultIncomeQuestionValues = () => {
    const householdMembers = data?.[2]?.members;
    const incomeMembers = data?.[5]?.incomeMembers;

    const incomeFromHouseholdMembers = householdMembers?.map((qm) => {
      return {
        id: qm.id,
        name: `${qm.firstName} ${qm.lastName}`,
        hasIncome: false,
      };
    });
    return (
      incomeMembers ?? (incomeFromHouseholdMembers as IncomeQuestionModel[])
    );
  };

  const defaultIncomeQuestionMembers = getDefaultIncomeQuestionValues();

  /** 5. DEFAULT INCOME QUESTION FORM ENDS */

  /** 6. DEFAULT INCOME TYPE FORM */
  const getDefaultIncomeTypesValues = () => {
    const incomeMembers = data?.[5]?.incomeMembers;
    const membersWithIncome = incomeMembers?.filter((i) =>
      JSON.parse(String(i.hasIncome)),
    );
    const incomeTypesMembers = data?.[6]?.incomeTypeMembers;

    const incomeFromHouseholdMembers = membersWithIncome?.map((qt) => {
      return {
        id: qt.id,
        name: qt.name,
        currentOrPartJob: CurrentJobEnum.NO,
        hasSeasonalJob: false,
        isSelfEmployed: false,
        otherIncomeSource: i18n.t("no"),
      };
    });

    return (
      incomeTypesMembers ?? (incomeFromHouseholdMembers as IncomeTypeModel[])
    );
  };

  const defaultIncomeTypeMembers = getDefaultIncomeTypesValues();

  /** 6. DEFAULT INCOME TYPE FORM ENDS */

  /** 7. DEFAULT DETAIL MEMBER JOB FORM */

  const getDefaultDetailJobMembers = () => {
    const incomeMembers = data?.[6]?.incomeTypeMembers;
    const detailJobMembers = data?.[7]?.detailJobMembers;

    const defaultDetailJobMembers = incomeMembers
      ?.map((dm) => {
        const hasCurrentJob = dm.currentOrPartJob !== CurrentJobEnum.NO;
        const hasSeasonalJob = dm.hasSeasonalJob;
        const isSelfEmployment = dm.isSelfEmployed;
        const hasOtherIncomeSource = i18n.t("no") !== dm.otherIncomeSource;

        if (
          !hasCurrentJob &&
          !hasSeasonalJob &&
          !isSelfEmployment &&
          !hasOtherIncomeSource
        )
          return null;

        const fillDefaultCurrentJobsByAnswer = {
          [CurrentJobEnum.NO]: 0,
          [CurrentJobEnum.YES]: 1,
        };

        const jobsQuantity =
          fillDefaultCurrentJobsByAnswer[dm.currentOrPartJob];

        const currentJobFields: CurrentJobModel = {
          employerName: "",
          employerEin: "",
          employerStreetAddress: "",
          employerCity: "",
          employerZipCode: "",
          employerState: "",
          wage: "",
          hoursPerWeek: null,
          amount: null,
        };

        const seasonalJobFields: SeasonalJobModel[] = [
          {
            employerName: "",
            employerEin: "",
            employerStreetAddress: "",
            employerCity: "",
            employerZipCode: "",
            employerState: "",
            seasonalIncome: null,
            expectedUnemployment: null,
          },
        ];

        const selfEmploymentFields: SelfEmploymentModel = {
          selfEmployedName: "",
          typeOfBusiness: "",
          businessStartDate: "",
          incomeAmount: null,
          lossAmount: null,
        };

        const otherIncomeSourceFields: OtherIncomeSourceModel = {
          typeOfIncome: i18n.t(dm.otherIncomeSource),
          howOftenReceived: "",
          amount: null,
          notTaxableInterestIncome: null,
          incomes: [
            {type: "", amount: null, frequency: ""},
            {type: "", amount: null, frequency: ""},
          ],
        };

        return {
          id: dm.id,
          name: dm.name,
          ...(hasCurrentJob && {
            currentJobs: Array(jobsQuantity).fill(currentJobFields),
          }),
          ...(hasSeasonalJob && {seasonalJobs: seasonalJobFields}),
          ...(isSelfEmployment && {selfEmployment: selfEmploymentFields}),
          ...(hasOtherIncomeSource && {
            otherIncomeSource: otherIncomeSourceFields,
          }),
        };
      })
      .filter((item) => item !== null);

    return (
      detailJobMembers ?? (defaultDetailJobMembers as DetailJobMemberModel[])
    );
  };
  const defaultDetailJobMembers = getDefaultDetailJobMembers();

  /** 7. DEFAULT DETAIL MEMBER JOB FORM ENDS */

  /** 8. PAI MEMBERS FORM **/

  const getDefaultPaiValues = () => {
    const paiForm = data?.[8];
    const detailJobMembers = data?.[7]?.detailJobMembers;

    const paiFromIncomeMembers = detailJobMembers?.map((pm) => {
      const {expectedIncome, projectedAnnualIncome} =
        calculatePaiMemberAnnualIncome(pm);

      return {
        id: pm.id,
        name: pm.name,
        expectedIncome: expectedIncome
          ? Number(expectedIncome?.toFixed())
          : null,
        adjustmentsIncome: 0,
        projectedAnnualIncome: projectedAnnualIncome
          ? Number(projectedAnnualIncome?.toFixed())
          : null,
      };
    });

    const defaultPaiForm = {
      paiMembers: paiFromIncomeMembers,
    };
    return paiForm ?? (defaultPaiForm as PAIFormFormType);
  };

  const defaultPaiMembers = getDefaultPaiValues();

  /** 8. PAI MEMBERS FORM ENDS**/

  /** 9. HEALTH COVERAGE MEMBER FORM */

  const getDefaultHealthCoverageValues = () => {
    const healthCoverageFromMemberList = data?.[2]?.members;
    const healthCoverageMemberList = data?.[9]?.healthCoverageMembers;

    const adjustmentIncomeFromIncomeMembers = healthCoverageFromMemberList?.map(
      (hc) => {
        return {
          id: hc.id,
          name: `${hc.firstName} ${hc.lastName}`,
          isEnrolledHealthCoverage: false,
          healthCoverage: "",
          policyHolderName: "",
          policyDateOfBirth: null,
          insuranceCompanyName: "",
          startDate: null,
          endDate: null,
          groupNumber: null,
          insurancePolicyName: "",
          policyCoverageList: "",
          policyNumber: null,
        };
      },
    );
    return (
      healthCoverageMemberList ??
      (adjustmentIncomeFromIncomeMembers as HealthCoverageModel[])
    );
  };

  const defaultHealthCoverageMembers = getDefaultHealthCoverageValues();

  /** 9. HEALTH COVERAGE MEMBER FORM ENDS */

  /** 10. IMMIGRATION FORM START */
  const getDefaultImmigrationValues = () => {
    const members = data?.[2]?.members;
    const immigrationMemberList = data?.[10]?.immigrationMembersList;

    const defaultImmigrationMembersList = members?.map((m) => {
      return {
        id: m.id,
        name: `${m.firstName} ${m.lastName}`,
        hasCitizenship: null,
        hasImmigrationStatus: null,
        documentType: null,
        alienId: null,
        expirationDate: null,
        entryDate: null,
        hasEnteredBeforeDate: null,
        hasLivedBeforeWithStatus: null,
        hadDifferentStatus: null,
        previousStatusCode: null,
        previousEntryDate: null,
      };
    });
    return (
      immigrationMemberList ??
      (defaultImmigrationMembersList as ImmigrationModel[])
    );
  };
  const defaultImmigrationMembers = getDefaultImmigrationValues();

  /** 10. IMMIGRATION FORM ENDS */

  /** 11. FOLLOWING QUESTIONS MEMBER FORM */

  const getDefaultFollowingQuestionsValues = () => {
    const followingQuestionsMembers = data?.[11];

    const followingQuestionDefault = {
      anyoneEnrolledInHealthCoverage: false,
      anyoneOfferedButNotEnrolledThroughAJob: false,
      isInJail: false,
      whoIsInJail: "",
      isDispositionOfCharges: false,
      whoIsDispositionOfCharges: "",
      isNeedPayingMedical: false,
      whoIsNeedPayingMedical: "",
      hasHealthConditions: false,
      whoHasHealthConditions: "",
      isBlindOrDisabled: false,
      whoIsBlindOrDisabled: "",
      hasDeterminedDisabledSsa: false,
      whoHasDeterminedDisabledSsa: "",
      isReceivingSsi: false,
      whoIsReceivingSsi: "",
      isNeedHomeCareHelp: false,
      whoIsNeedHomeCareHelp: "",
      hasOngoingMedicalBills: false,
      whoHasOngoingMedicalBills: "",
      isGettingServiceCvt: false,
      whoIsGettingServiceCvt: "",
      isOutsideOfState: false,
      whoIsOutsideOfState: "",
      dateLeft: null,
      hasReturnedFromMilitaryDutyTour: false,
      expectedReturnDate: null,
      whoHasReturnedFromMilitaryDutyTour: "",
      lastActiveDate: null,
      isHomeless: false,
    };
    return (
      followingQuestionsMembers ??
      (followingQuestionDefault as FollowingQuestionsModel)
    );
  };
  const defaultFollowingQuestionsMembers = getDefaultFollowingQuestionsValues();

  /** 11. FOLLOWING QUESTIONS MEMBER FORM ENDS */

  /** 12 DEFAULT RENEWING COVERAGE FORM */
  const getDefaultRenewingCoverage = () => {
    const renewingData = data?.[12];
    const renewingCoverageEmptyData = {
      agreementDuration: "1_year",
    };
    return renewingData ?? (renewingCoverageEmptyData as RenewingCoverageModel);
  };

  const defaultRenewingCoverage = getDefaultRenewingCoverage();

  /** 12 DEFAULT RENEWING COVERAGE FORM ENDS */

  /** 13. SECURITY QUESTIONS FORM */
  const securityQuestionsValues = data?.[13];

  const defaultSecurityQuestionPlaceholder = {
    grandfatherOccupation: "",
    childhoodFriendName: "",
    partnerMeetingLocation: "",
    parentsMeetingLocation: "",
    preferredRetirementCity: "",
    favoriteSportsTeam: "",
    firstCarModel: "",
    favoriteFood: "",
    password: "",
  };

  const defaultSecurityQuestionsFormValues =
    securityQuestionsValues ?? defaultSecurityQuestionPlaceholder;
  /** 13. SECURITY QUESTIONS FORM ENDS */

  return {
    defaultSecurityQuestionsFormValues,
    defaultApplicantMember,
    defaultHouseholdMembers,
    defaultPregnancyMembers,
    defaultTaxFillingMembers,
    defaultIncomeQuestionMembers,
    defaultIncomeTypeMembers,
    defaultDetailJobMembers,
    defaultPaiMembers,
    defaultHealthCoverageMembers,
    defaultFollowingQuestionsMembers,
    defaultRenewingCoverage,
    defaultImmigrationMembers,

  };
};
